import React from 'react';
import { Users, Coins, DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export type TimeRange = 'hourly' | 'daily' | 'weekly' | 'monthly';
export type ChartMetric = 'users' | 'gt' | 'income';

interface ChartDataPoint {
  date: string;
  users: number;
  gt: number;
  income: number;
}

export interface PeriodStats {
  users: {
    total: number;
    new: number;
    active: number;
    change: number;
  };
  gt: {
    circulating: number;
    earned: number;
    spent: number;
    change: number;
  };
  income: {
    total: number;
    purchases: number;
    averageOrder: number;
    change: number;
  };
}

interface AnalyticsChartProps {
  data: ChartDataPoint[];
  activeMetric: ChartMetric;
  onMetricChange: (metric: ChartMetric) => void;
  periodStats: PeriodStats;
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  data,
  activeMetric,
  onMetricChange,
  periodStats
}) => {
  const metricConfig = {
    users: {
      label: 'Users',
      icon: Users,
      color: '#6366f1',
      bgColor: 'bg-primary/10',
      textColor: 'text-primary'
    },
    gt: {
      label: 'GT Tokens',
      icon: Coins,
      color: '#22c55e',
      bgColor: 'bg-success/10',
      textColor: 'text-success'
    },
    income: {
      label: 'Revenue',
      icon: DollarSign,
      color: '#3b82f6',
      bgColor: 'bg-info/10',
      textColor: 'text-info'
    }
  };

  const currentConfig = metricConfig[activeMetric];

  const renderStatsTable = () => {
    switch (activeMetric) {
      case 'users':
        return (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Total Users</p>
              <p className="text-2xl font-bold">{periodStats.users.total.toLocaleString()}</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">New Users</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-success">{periodStats.users.new.toLocaleString()}</p>
                {periodStats.users.change !== 0 && (
                  <span className={`flex items-center text-xs font-medium ${periodStats.users.change >= 0 ? 'text-success' : 'text-error'}`}>
                    {periodStats.users.change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(periodStats.users.change)}%
                  </span>
                )}
              </div>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Active Users</p>
              <p className="text-2xl font-bold">{periodStats.users.active.toLocaleString()}</p>
            </div>
          </div>
        );
      case 'gt':
        return (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Circulating</p>
              <p className="text-2xl font-bold">{periodStats.gt.circulating.toLocaleString()}</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Earned</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-success">+{periodStats.gt.earned.toLocaleString()}</p>
                {periodStats.gt.change !== 0 && (
                  <span className={`flex items-center text-xs font-medium ${periodStats.gt.change >= 0 ? 'text-success' : 'text-error'}`}>
                    {periodStats.gt.change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(periodStats.gt.change)}%
                  </span>
                )}
              </div>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Spent</p>
              <p className="text-2xl font-bold text-error">-{periodStats.gt.spent.toLocaleString()}</p>
            </div>
          </div>
        );
      case 'income':
        return (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Total Revenue</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold">£{periodStats.income.total.toLocaleString()}</p>
                {periodStats.income.change !== 0 && (
                  <span className={`flex items-center text-xs font-medium ${periodStats.income.change >= 0 ? 'text-success' : 'text-error'}`}>
                    {periodStats.income.change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(periodStats.income.change)}%
                  </span>
                )}
              </div>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Purchases</p>
              <p className="text-2xl font-bold">{periodStats.income.purchases.toLocaleString()}</p>
            </div>
            <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-1">Avg. Order</p>
              <p className="text-2xl font-bold">£{periodStats.income.averageOrder.toFixed(2)}</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="card p-5">
      {/* Metric Tabs */}
      <div className="flex gap-2 mb-6">
        {(Object.keys(metricConfig) as ChartMetric[]).map((metric) => {
          const config = metricConfig[metric];
          const Icon = config.icon;
          const isActive = activeMetric === metric;

          return (
            <button
              key={metric}
              onClick={() => onMetricChange(metric)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                isActive
                  ? `${config.bgColor} ${config.textColor}`
                  : 'bg-surface-light dark:bg-gray-800 text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light'
              }`}
            >
              <Icon size={18} />
              <span>{config.label}</span>
            </button>
          );
        })}
      </div>

      {/* Stats Table */}
      <div className="mb-6">
        {renderStatsTable()}
      </div>

      {/* Chart */}
      <div className="h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
              formatter={(value: number) => {
                if (activeMetric === 'income') return [`£${value.toFixed(2)}`, 'Revenue'];
                if (activeMetric === 'gt') return [value.toLocaleString(), 'GT'];
                return [value.toLocaleString(), 'Users'];
              }}
            />
            <Line
              type="monotone"
              dataKey={activeMetric}
              name={currentConfig.label}
              stroke={currentConfig.color}
              strokeWidth={2}
              dot={false}
              fill={`${currentConfig.color}20`}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
